import { useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { formatMoney, hoy } from '@abastece/shared';
import type { Recarga } from '@abastece/core';
import { db } from '../db/dexie.js';
import { rangoDe } from '../lib/historial.js';
import {
  ESTADOS_RECARGA,
  FORMAS_DE_RECARGA,
  SIN_FILTROS_RECARGAS,
  filtrosDeRecargas,
  ordenarRecargas,
  recargaCoincide,
  resumenRecargas,
  type EstadoRecarga,
  type FiltroRecargas,
} from '../lib/historialDinero.js';
import { FilaRecarga, diaDeLista } from '../ui/FilasDeDinero.js';
import { Desplegable, FilaDesplegables, FiltroFechas, FiltrosEnLinea } from '../ui/FiltrosHistorial.js';
import { Ico } from '../ui/Iconos.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { useT, type Clave } from '../i18n/index.js';

/** How many show at a time: the latest, and «Ver más» for the rest. */
const LOTE = 50;

/** A way of paying for airtime, as its form names it. */
const NOMBRE_FORMA: Record<(typeof FORMAS_DE_RECARGA)[number], Clave> = {
  efectivo_cordobas: 'recargas.efectivo',
  transferencia: 'pago.transferencia',
  billetera: 'gastos.billeteraMovil',
};
const nombreForma = (forma: string): Clave | undefined => NOMBRE_FORMA[forma as keyof typeof NOMBRE_FORMA];

/**
 * «Recargas anteriores»: every top-up sold, found by day, carrier, way of paying, whether the
 * transfer came, and above all by the phone number, as a customer back at the counter says it.
 * «Recargas» keeps the latest thirty while a till is open; this needs none.
 */
export function RecargasAnteriores() {
  const t = useT();
  const hoyEs = hoy();
  const recargas = useLiveQuery(() => db.recargas.toArray(), [], undefined);
  const [filtro, setFiltro] = useState<FiltroRecargas>(SIN_FILTROS_RECARGAS);
  const [cuantas, setCuantas] = useState(LOTE);

  const rango = rangoDe(filtro.periodo, hoyEs, filtro);
  const puestos = filtrosDeRecargas(filtro, rango);
  const filtrando = filtro.texto.trim() !== '' || puestos.length > 0;
  const cambiar = (cambio: Partial<FiltroRecargas>) => {
    setFiltro((f) => ({ ...f, ...cambio }));
    setCuantas(LOTE);
  };
  const encontradas = useMemo(
    () => ordenarRecargas((recargas ?? []).filter((r) => recargaCoincide(r, filtro, rango))),
    // `rango` is worked out afresh each time: its two days are what it is.
    [recargas, filtro, rango.desde, rango.hasta],
  );
  const resumen = resumenRecargas(encontradas);
  /** The carriers sold, as they were written: those are what the dropdown can find. */
  const operadoras = useMemo(() => [...new Set((recargas ?? []).map((r) => r.operadora))].sort(), [recargas]);
  const cuantasHay = recargas?.length ?? 0;
  const detalle = (r: Recarga) => {
    const forma = nombreForma(r.formaPago);
    return [diaDeLista(r.fecha), forma ? t(forma) : '', r.referencia ?? ''].filter(Boolean).join(' · ');
  };

  return (
    <Pagina titulo={t('recargasHist.titulo')} subtitulo={t('recargasHist.registradas', { n: cuantasHay })} volverA="/recargas">
      {(filtrando || cuantasHay > 0) && (
        <FiltrosEnLinea
          buscar={{ texto: filtro.texto, onTexto: (texto) => cambiar({ texto }), placeholder: t('recargasHist.buscar') }}
          hayPuestos={puestos.length > 0}
          onQuitarTodos={() => cambiar({ ...SIN_FILTROS_RECARGAS, texto: filtro.texto })}
        >
          <FiltroFechas periodo={filtro.periodo} desde={filtro.desde} hasta={filtro.hasta} onCambio={cambiar} />
          <FilaDesplegables>
            <Desplegable
              clave="operadora"
              etiqueta={t('recargas.operadora')}
              value={filtro.operadora}
              onChange={(operadora) => cambiar({ operadora })}
              elegido={filtro.operadora || undefined}
            >
              <option value="">{t('historial.todas')}</option>
              {operadoras.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </Desplegable>
            <Desplegable
              clave="forma"
              etiqueta={t('historial.forma')}
              value={filtro.forma}
              onChange={(forma) => cambiar({ forma })}
              elegido={filtro.forma ? t(nombreForma(filtro.forma) ?? 'recargas.efectivo') : undefined}
            >
              <option value="">{t('historial.todas')}</option>
              {FORMAS_DE_RECARGA.map((f) => (
                <option key={f} value={f}>
                  {t(NOMBRE_FORMA[f])}
                </option>
              ))}
            </Desplegable>
            <Desplegable
              clave="estado"
              etiqueta={t('historial.estado')}
              value={filtro.estado}
              onChange={(v) => cambiar({ estado: v as EstadoRecarga })}
              elegido={filtro.estado === 'todas' ? undefined : t(`recargasHist.estado.${filtro.estado}`)}
            >
              {ESTADOS_RECARGA.map((e) => (
                <option key={e} value={e}>
                  {t(`recargasHist.estado.${e}`)}
                </option>
              ))}
            </Desplegable>
          </FilaDesplegables>
        </FiltrosEnLinea>
      )}

      {filtrando && encontradas.length > 0 && (
        <p className="tabular mb-3 text-sm text-slate-400" data-historial-resumen>
          {t('recargasHist.resumen', { n: resumen.n, vendido: formatMoney(resumen.vendido), comision: formatMoney(resumen.comision) })}
        </p>
      )}

      {/* Nothing said while it is read: «nothing matches» was only «not read yet». */}
      {recargas === undefined ? null : encontradas.length === 0 ? (
        filtrando ? (
          <Vacio mensaje={t('recargasHist.ninguna')} icono="buscar">
            <button type="button" className="btn-secundario" onClick={() => cambiar(SIN_FILTROS_RECARGAS)}>
              <Ico icono="reiniciar" />
              {t('historial.quitarBusqueda')}
            </button>
          </Vacio>
        ) : (
          <Vacio mensaje={t('recargasHist.vacio')} icono="recargas" />
        )
      ) : (
        <ul className="tarjeta flex flex-col divide-y divide-slate-800 py-1" data-historial-lista>
          {encontradas.slice(0, cuantas).map((r) => (
            <FilaRecarga key={r.id} recarga={r} detalle={detalle(r)} />
          ))}
        </ul>
      )}
      {encontradas.length > cuantas && (
        <button type="button" className="btn-secundario mt-4 w-full" onClick={() => setCuantas((n) => n + LOTE)}>
          <Ico icono="desplegar" />
          {t('comun.verMas')}
        </button>
      )}
    </Pagina>
  );
}
